<template>
  <div class="container workshops">
    <div class="row mb-45">
      <div class="col-lg-8 col-12">
        <h1>Wellbeing Workshops</h1>
      </div>
    </div>

    <div v-if="loading && !workshops.length" class="row">
      <Loader style="width: 100%;" />
    </div>

    <div v-else class="row">
      <div class="col-12 d-lg-none">
        <button class="modal-filters btn-secondary large-filters-btn" type="button" @click="showFilters()">
          <font-awesome-icon
            :icon="['fas', 'sliders-v']"
            class="icon alt"
          />
          <span>Filters</span>
        </button>
      </div>
      <div class="col-12 col-lg-8">
        <div v-if="workshops.length === 0" class="dashboard-card workshops-list">
          No workshops were found.
          <div class="d-lg-none">
            <button class="btn modal-filters btn-primary mt-4" type="button" @click="showFilters()">
              Change Filters
            </button>
          </div>
        </div>

        <div v-else class="dashboard-card workshops-list">
          <div v-for="workshop in workshops" :key="workshop.id" class="session">
            <div class="row cursor-pointer" @click="viewWorkshop(workshop)">
              <div class="col-12 col-lg-8">
                <h3>{{ workshop.topic}}</h3>
                <h5 v-if="workshop.host">{{ workshop.host.name }}</h5>
                <p>{{ workshop.start_time | date }}</p>
                <p>Location: {{ workshop.in_person_location || 'Online' }}</p>
                <span class="spot-number d-lg-none">
                  <span v-if="workshop.joined">Joined!</span>
                  <span v-else>
                    {{ workshop.num_spots_left }}
                    <span v-if="workshop.num_spots_left === 1">Spot</span>
                    <span v-else>Spots</span>
                    Left!
                  </span>
                </span>
              </div>
              <div class="col-12 d-none d-lg-block col-lg-4 text-right">
                <ul class="pills">
                  <li v-if="workshop.joined">Joined!</li>
                  <li v-else>
                    {{ workshop.num_spots_left }}
                    <span v-if="workshop.num_spots_left === 1">Spot</span>
                    <span v-else>Spots</span>
                    Left!
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div>
            <Loader v-if="loading" style="width: 100%" />
            <button v-else-if="pageToken" class="btn" @click="loadWorkshops">
              Load Next
            </button>
            <div v-else>
              <p>
                <font-awesome-icon :icon="['fas', 'check-circle']" class="icon alt" />
                All Results Loaded
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="d-none d-lg-block col-lg-4">
        <div class="dashboard-card filter-sidebar">
          <workshops-list-filter
            :value="filters"
            @input="resetFilters"
          />
        </div>
      </div>
    </div>

    <workshop-view-modal
      ref="workshopViewModal"
      @withdraw="withdrawFromWorkshop"
      @register="registerToWorkshop"
    />

    <confirm-modal
      ref="confirmWithdrawModal"
      title="Withdraw from workshop"
      :confirm-body="withdrawConfirmMessage"
      :on-confirm="doWithdraw"
      cancel-text="No"
    />

    <b-modal
      id="filters-modal"
      ref="filtersModal"
    >
      <workshops-list-filter
        v-model="modalFilters"
      />

      <template
        slot="modal-footer"
      >
        <button
          class="btn"
          type="button"
          @click="hideFilters()"
        >
          Cancel
        </button>
        <button
          class="btn btn--primary"
          type="button"
          @click="doModalFilters()"
        >
          Filter
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import WorkshopViewModal from './components/WorkshopViewModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import Loader from '@/components/Loader';
import WorkshopClient from '@/api/WorkshopClient';
import moment from 'moment';
import WorkshopsListFilter from './components/WorkshopsListFilter.vue'
import WorkshopView from './WorkshopView'

export default {
  name: 'WorkshopsList',
  components: {
    Loader,
    WorkshopViewModal,
    WorkshopsListFilter,
    ConfirmModal,
  },
  data() {
    return {
      workshopClient: new WorkshopClient(this.$http),
      selectedWorkshop: null,
      workshops: [],
      pageToken: null,
      loading: false,

      filters: {
        specialty: '',
        start_time_after: '',
        start_time_before: '',
        specialty_ids: [],
      },
      modalFilters: {
        specialty: '',
        start_time_after: '',
        start_time_before: '',
        specialty_ids: [],
      },
    };
  },
  computed: {
    withdrawConfirmMessage() {
      if (!this.selectedWorkshop) {
        return 'Are you sure you want to withdraw from this workshop?';
      }

      const start_time = moment(this.selectedWorkshop.start_time);
      const now = moment();
      if (start_time.diff(now, 'hours') > 24) {
        return 'Are you sure you want to withdraw from this workshop? You will receive a full refund.';
      }

      return 'Are you sure you want to withdraw from this workshop? You will NOT receive a refund. (Within 24 hours of workshop start)'
    },
  },
  async mounted() {
    const { id } = this.$route.params;

    this.loadWorkshops(id);
  },
  methods: {
    showFilters() {
      this.modalFilters = { ...this.filters };
      this.$refs.filtersModal.show();
    },
    doModalFilters() {
      this.filters = { ...this.modalFilters };
      this.hideFilters();
      this.pageToken = null;
      
      return this.loadWorkshops();
    },
    hideFilters() {
      this.$refs.filtersModal.hide();
    },
    resetFilters(filters) {
      this.filters = filters;
      this.pageToken = null;
      
      return this.loadWorkshops();
    },
    
    async registerToWorkshop({ workshop, auto_pay_with_credit }) {
      this.$refs.workshopViewModal.hide();
      this.selectedWorkshop = workshop;
      
      if (workshop.cost && this.$store.getters.currentUser.num_pending_payments > 0) {
        return this.$store.commit('setGlobalAlert', {
          msg: 'To schedule a session you must first complete your pending payments.', error: true,
        });
      }

      const user_id = this.$store.getters.currentUser.id;

      try {
        this.loading = true;
        const res = await this.workshopClient.register(user_id, workshop.id, auto_pay_with_credit);
        this.$store.commit('setGlobalAlert', {
          msg: 'You have registered successfully!'
        });

        this.loadWorkshops();
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Something went wrong. Please try again.', error: true,
        });
      } finally {
        this.loading = false;
      }
    },

    withdrawFromWorkshop(workshop) {
      this.$refs.workshopViewModal.hide();
      this.selectedWorkshop = workshop;
      this.$refs.confirmWithdrawModal.show();
    },
    async doWithdraw() {
      try {
        this.$refs.confirmWithdrawModal.hide();
        await this.workshopClient.withdrawByUser(this.selectedWorkshop.id, this.$store.getters.currentUser.id)
        this.$store.commit('setGlobalAlert', {
          msg: 'You have successfully withdrawn from the workshop.',
        });
        this.loadWorkshops();
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to withdraw from workshop. Please try again!', error: true,
        });
      }
    },
    async loadWorkshops(id) {
      try {
        this.loading = true;

        const filters = {
          userIsAttending: undefined,
        };

        if (this.filters.specialty_ids) {
          filters.related_topics = this.filters.specialty_ids.map(s => s.id);
        }

        if (this.filters.start_time_after) {
          filters.start_time_after = moment(this.filters.start_time_after).startOf('day').toISOString();
        }

        if (this.filters.start_time_before) {
          filters.start_time_before = moment(this.filters.start_time_before).endOf('day').toISOString()
        }

        if (this.pageToken == null) {
          this.workshops.length = 0;
        }

        const response = await this.workshopClient.listForUser(
          this.pageToken,
          this.$store.getters.currentUser.id,
          filters.start_time_after,
          filters.start_time_before,
          filters.related_topics,
          filters.userIsAttending);

        this.pageToken = response.nextPageToken;

        const mapped = (response.workshops || []).map(this.toWorkshopView)
        for(const workshop of mapped){
          this.workshops.push(workshop);
        }

        if (id) {
          let workshop = this.workshops.find(x => x.id == id)
          if (workshop) {
            this.viewWorkshop(workshop)
          }
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loading = false;
      }
    },
    toWorkshopView(val, index, arr) {
      return new WorkshopView(val)
    },
    viewWorkshop(workshop) {
      this.selectedWorkshop = workshop;
      this.$refs.workshopViewModal.show(workshop);
    },

    specialtyChanged(newTags) {
      this.filters.specialty_ids = newTags;
      this.loadWorkshops();
    },
    modalSpecialtyChanged(newTags) {
      this.modalFilters.specialty_ids = newTags;
      this.loadWorkshops();
    },
  },
};
</script>

<style lang="scss" scoped>
.workshops {
  .workshops-list {
    .upper {
      margin-bottom: 30px;
    }

    .nav-buttons {
      position: absolute;
      right: 42px;
      top: 28px;

      @media (max-width: $xs-max) {
        position: static;
        margin-top: 20px;
      }

      button {
        background: none;
        border: none;
        color: white;
        font-size: 30px;
        width: 35px;

        @media (max-width: $md-max) {
          width: 50px;
        }
      }
    }

    .session {
      @include border-radius($border-radius);

      border: solid 1px rgba(white, 0.5);
      padding: 30px;
      margin-bottom: 10px;

      p {
        color: rgba(white, 0.75);
        font-size: 18px;
        margin-bottom: 20px;
      }

      .spot-number {
        color: $green;
        margin-bottom: 30px;
        display: block;
        font-weight: bold;
      }
    }
  }

  .replacement-label {
    text-transform: uppercase;
    font-size: 10px;
    opacity: 0.75;
    letter-spacing: 0.1rem;
    margin-bottom: 5px;
    color: white;
  }
}

.cursor-pointer {
  cursor: pointer !important;
}

.create-button {
  display: inline-block;
  margin-top: 7px;
}
</style>
